/* recording.js */

import { useEffect, useRef, useState } from "react";

export function useVoiceRecorder({ onComplete } = {}) {
  const mediaStreamRef    = useRef(null);
  const recorderRef       = useRef(null);
  const audioChunksRef    = useRef([]);
  const recordingTimerRef = useRef(null);
  const startTimeRef      = useRef(null);
  const accumulatedRef    = useRef(0);   // ms accumulated across pause/resume cycles
  const cancelledRef      = useRef(false);
  const isStartingRef     = useRef(false);

  // ── Use refs for live booleans so callbacks never close over stale state ──
  const isRecordingRef = useRef(false);
  const isPausedRef    = useRef(false);

  const [isRecording,      setIsRecording]      = useState(false);
  const [isPaused,         setIsPaused]         = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [previewUrl,       setPreviewUrl]       = useState(null);

  /* ── Keep refs in sync with state ── */
  const _setIsRecording = (v) => { isRecordingRef.current = v; setIsRecording(v); };
  const _setIsPaused    = (v) => { isPausedRef.current    = v; setIsPaused(v);    };

  /* ── Cleanup on unmount ── */
  useEffect(() => {
    return () => {
      _clearTimer();
      _forceStopAllTracks();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try { recorderRef.current.stop(); } catch (_) {}
      }
      recorderRef.current    = null;
      audioChunksRef.current = [];
    };
  }, []);

  /* ───────────────── Helpers ───────────────── */

  function _clearTimer() {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }

  /**
   * Start (or restart after resume) the display timer.
   * accumulatedRef already holds ms from previous segments.
   * We just reset startTimeRef to "now" and let the interval add on top.
   */
  function _startTimer() {
    _clearTimer();
    startTimeRef.current = Date.now();
    recordingTimerRef.current = setInterval(() => {
      const totalMs = accumulatedRef.current + (Date.now() - startTimeRef.current);
      setRecordingSeconds(Math.floor(totalMs / 1000));
    }, 200);
  }

  function _resetState() {
    _clearTimer();
    _setIsRecording(false);
    _setIsPaused(false);
    setRecordingSeconds(0);
    setPreviewUrl(null);
    startTimeRef.current   = null;
    accumulatedRef.current = 0;
  }

  function _forceStopAllTracks() {
    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => {
          try { t.stop(); t.enabled = false; } catch (_) {}
        });
        mediaStreamRef.current = null;
      }
      if (recorderRef.current?.stream) {
        recorderRef.current.stream.getTracks().forEach((t) => {
          try { t.stop(); t.enabled = false; } catch (_) {}
        });
        try { recorderRef.current.stream = null; } catch (_) {}
      }
    } catch (e) {
      // forceStop failed
    }
  }

  function _killGlobalMic() {
    try {
      navigator.mediaDevices?.getUserMedia({ audio: true })
        .then((s) => s.getTracks().forEach((t) => t.stop()))
        .catch(() => {});
    } catch (_) {}
  }

  /* ───────────────── startRecording ───────────────── */

  const startRecording = async () => {
    // Use REF guard — state is stale inside async callbacks
    if (isStartingRef.current || isRecordingRef.current || isPausedRef.current) {
      return;
    }
    isStartingRef.current = true;

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Media recording not supported in this browser");
      }

      cancelledRef.current   = false;
      audioChunksRef.current = [];
      accumulatedRef.current = 0;   // fresh session

      _forceStopAllTracks();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        _forceStopAllTracks();

        if (cancelledRef.current) {
          audioChunksRef.current = [];
          recorderRef.current    = null;
          return;
        }

        if (!audioChunksRef.current.length) {
          return;
        }

        try {
          const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          const file = new File([blob], `voice_${Date.now()}.webm`, { type: blob.type });
          const url  = URL.createObjectURL(blob);
          if (onComplete) await onComplete(file, url);
        } catch (e) {
          // onstop error
        } finally {
          audioChunksRef.current = [];
          recorderRef.current    = null;
        }
      };

      recorder.start(100); // chunk every 100 ms → enables live preview snapshots

      _setIsRecording(true);
      _setIsPaused(false);
      setRecordingSeconds(0);
      setPreviewUrl(null);
      _startTimer();  // ← starts the tick

    } catch (err) {
      _resetState();
      _forceStopAllTracks();
    } finally {
      isStartingRef.current = false;
    }
  };

  /* ───────────────── pauseRecording ───────────────── */

  const pauseRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording") {
      return;
    }

    try {
      // 1. Freeze accumulated time BEFORE pausing so math stays correct on resume
      accumulatedRef.current += Date.now() - (startTimeRef.current ?? Date.now());
      _clearTimer();

      // 2. Pause the MediaRecorder
      recorder.pause();

      // 3. Snapshot current chunks → preview blob the user can play back
      if (audioChunksRef.current.length > 0) {
        const snapshot = new Blob([...audioChunksRef.current], { type: "audio/webm" });
        if (snapshot.size > 0) {
          const url = URL.createObjectURL(snapshot);
          setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
          });
        }
      }

      // 4. ⚠️ CRITICAL: flip isRecording → false, isPaused → true
      //    This makes ChatInput switch from the recording row to the paused row
      _setIsRecording(false);
      _setIsPaused(true);

    } catch (e) {
      // pauseRecording error
    }
  };

  /* ───────────────── resumeRecording ───────────────── */

  const resumeRecording = () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused") {
      return;
    }

    try {
      recorder.resume();

      // Flip back: recording = true, paused = false, restart display timer
      _setIsRecording(true);
      _setIsPaused(false);
      _startTimer(); // accumulatedRef still holds everything before this segment

    } catch (e) {
      // resumeRecording error
    }
  };

  /* ───────────────── stopRecording ───────────────── */

  const stopRecording = () => {
    cancelledRef.current = false;
    _clearTimer();
    _setIsRecording(false);
    _setIsPaused(false);
    setPreviewUrl(null);

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        // If paused, must resume before stopping so all buffered chunks flush
        if (recorder.state === "paused") recorder.resume();
        recorder.stop();
        setTimeout(() => { _forceStopAllTracks(); _killGlobalMic(); }, 350);
      } catch (err) {
        _forceStopAllTracks();
        _killGlobalMic();
      }
    } else {
      _forceStopAllTracks();
      _killGlobalMic();
    }
  };

  /* ───────────────── cancelRecording ───────────────── */

  const cancelRecording = () => {
    cancelledRef.current   = true;
    audioChunksRef.current = [];
    _resetState();

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.ondataavailable = null;
        if (recorder.state === "paused") recorder.resume();
        recorder.stop();
      } catch (_) {}
    }

    setTimeout(() => {
      _forceStopAllTracks();
      _killGlobalMic();
      recorderRef.current = null;
    }, 200);
  };

  return {
    isRecording,
    isPaused,
    recordingSeconds,
    previewUrl,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  };
}